// DR-013 — Web session resilience. Registered ONCE at bootstrap (index.js).
//
// The Firebase SDK's internal proactive token refresh does NOT run while the machine is asleep, and
// there were no visibilitychange/online listeners, so after a laptop wake or network flap the tab
// could sit on a stale token / dead socket until something failed. This installs:
//   - visibilitychange/focus  -> on resume: single-flight forced token refresh, then broadcast recover
//   - online                  -> on network restore: same
//   - a proactive near-expiry timer (every ~15 min while visible) -> forced refresh
// It never logs out and never mutates data; it only refreshes the token and emits `crm:session-recover`
// so the WebSocket layer (and any listener) can reconnect with a fresh credential.
import { forceRefreshIdToken, auth } from './firebase';

export const SESSION_RECOVER_EVENT = 'crm:session-recover';

let _installed = false;
let _refreshTimer = null;
let _lastRecoverAt = 0;

function broadcastRecover(reason) {
  try {
    window.dispatchEvent(new CustomEvent(SESSION_RECOVER_EVENT, { detail: { reason, at: Date.now() } }));
  } catch { /* no-op */ }
}

// Coalesce bursts (e.g. focus+visibilitychange+online firing together on wake) to one refresh.
async function recover(reason) {
  if (!auth?.currentUser) return;           // signed out -> nothing to recover
  const now = Date.now();
  if (now - _lastRecoverAt < 3000) return;  // debounce
  _lastRecoverAt = now;
  const t = await forceRefreshIdToken();    // single-flight; no stampede
  if (t) broadcastRecover(reason);
}

export function installSessionResilience() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  const onVisible = () => { if (document.visibilityState === 'visible') recover('resume'); };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', () => recover('focus'));
  window.addEventListener('online', () => recover('online'));

  // Proactive: refresh well before the ~1h expiry so an always-open tab never carries a stale token.
  const PROACTIVE_MS = 15 * 60 * 1000;
  _refreshTimer = setInterval(() => {
    if (document.visibilityState === 'visible' && auth?.currentUser) forceRefreshIdToken();
  }, PROACTIVE_MS);
}

export default installSessionResilience;
